import { BreadcrumbItem } from 'components/atoms/Breadcrumb';
import Plans from 'components/organism/TradePromoOptimization/Plans';
import Reports from 'components/organism/TradePromoOptimization/Reports';
import Tools from 'components/organism/TradePromoOptimization/Tools';
import { tradePromoOptimizationUrls } from 'config/urls';
import { useFetchKpiConfigData } from 'hooks/tradePromoOptimization/useFetchKpiConfigData';
import {
    Dispatch,
    FC,
    MutableRefObject,
    SetStateAction,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Modal } from 'antd';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DimensionDropdownType, tabsType } from 'types/pricingSimulator';
import {
    DimensionConfigType,
    PromoTactic,
    PromoType,
    PromoEvent,
    PromoEventKPIs,
    SelectedPlanInfoType,
    Metric,
    ProductPPG,
    KpiConfigType,
    PromoConfigDictionary,
    ItemWithEditable,
    RbacType,
    EventLibraryEvent,
    PlaybookKpiConfigType,
    PopUpErrorObject,
    EventViewType,
    PlanSummarySubTab,
    PlanEditMetaType,
    OptimizationPollingStatus,
    InfeasibilityPollingStatus,
    InfeasibilityConstraintGroup,
    DivisionMetricMapping,
    ValidatePlanPollingStatus,
} from 'types/tradePromoOptimization';
import { compareUsers, getConfig, getTabFromUrl, sendNotification } from 'utils/helperMethods';
import { UserContext } from './ConfigContext';
import { useFetchPromoKPIsConfigData } from 'hooks/tradePromoOptimization/useFetchPromoKpisConfigData';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import dayjs from 'dayjs';
import { queryClient } from 'services/reactQueryService';
import { getPromoConfigDataParser } from 'parser/tradePromoOptimization/eventsParser';
import { NEW_EVENT_PROMO_ID } from 'config/constants';
import { MetricsApiTriggerPoints } from 'components/molecules/TradePromoOptimization/AddEventModal/Promos';
import { tpmTpoTabComponent } from 'components/organism/TradePromoOptimization/UpdateOrViewTPO';
import _ from 'lodash';
import {
    TPO_APPROVAL_PUBLISH_FEATURE_NAME,
    TPO_APPROVAL_PUBLISH_TOGGLE_QUERY_KEY,
    tpoPromoMetricKeys,
} from 'utils/Enums';
import {
    getPromoMetricByNameKeyName,
    handleMetricInputChange,
    handleRecommendSuccessDefaultValue,
    resetEditableSpendsToZero,
    sortPPGsToExitingOrder,
} from 'components/molecules/TradePromoOptimization/tpoHelperMethods';
import axios from 'axios';
import { useMutation, UseMutationResult, useQuery } from '@tanstack/react-query';
import useTrackTabChange from 'hooks/general/useTrackTabChange';
import utc from 'dayjs/plugin/utc';
import { PromoInfoFormFieldNames } from 'constants/tpo';
dayjs.extend(utc);

export const initialPlanEditMetaData = {
    editingUser: null,
    sessionExpiresInSecs: null,
    startedAt: null,
    planId: null,
};

// Polling interval (ms) for the TPM refresh status check inside the TPO context.
// Update this single constant to change how frequently the status is polled.
export const TPM_REFRESH_STATUS_POLL_INTERVAL_MS = 7000;

/** Cross-tab signal: plan content changed (upsert/delete). Other tabs listen via `storage`. */
export const getPlanStaleBroadcastKey = (planId: string | number) => `tpo_plan_stale_${planId}`;

const TPODataContext = createContext<{
    //view mode
    viewMode: boolean;
    isCurrentPromoFormDisabled: boolean;

    //tabs
    activeTab: null | tabsType;
    tabs: null | tabsType[];
    setActiveTab: Function;

    tpmTpoActiveTab: null | tabsType;
    setTpmTpoActiveTab: Function;

    //breadcrumbs
    breadcrumbItems: BreadcrumbItem[];

    //dimension config state
    dimensionConfigData: null | DimensionConfigType;
    setDimensionConfigData: Dispatch<SetStateAction<DimensionConfigType | null>>;

    kpiConfigData: null | KpiConfigType;
    kpiRawData: any[] | null;
    playbookKpiConfigData: null | PlaybookKpiConfigType;
    configDataExists: boolean;
    kpiConfigLoading: boolean;

    //selected plan info and plan related state
    selectedPlanInfo: null | SelectedPlanInfoType;
    setSelectedPlanInfo: Dispatch<SetStateAction<SelectedPlanInfoType | null>>;
    eventViewType: EventViewType;
    setEventViewType: Dispatch<SetStateAction<EventViewType>>;
    planSummarySubTab: PlanSummarySubTab;
    setPlanSummarySubTab: Dispatch<SetStateAction<PlanSummarySubTab>>;
    enableBumpReport: boolean;
    setEnableBumpReport: Dispatch<SetStateAction<boolean>>;
    showCalenderChanges: boolean;
    setShowCalenderChanges: Dispatch<SetStateAction<boolean>>;
    showChanges: boolean;
    setShowChanges: Dispatch<SetStateAction<boolean>>;
    isToolsViewAbsolute: boolean;
    setIsToolsViewAbsolute: Dispatch<SetStateAction<boolean>>;

    // selected event info
    selectedEventInfo: PromoEvent | null;
    setSelectedEventInfo: Function;
    selectedEventLibraryEventInfo: EventLibraryEvent | null;
    setSelectedEventLibraryEventInfo: Function;
    updateSelectedEventProperty: <K extends keyof PromoEvent>(key: K, value: PromoEvent[K]) => void;
    updatePlanProperty: <K extends keyof SelectedPlanInfoType>(key: K, value: SelectedPlanInfoType[K]) => void;
    promoActiveTab: string | null;
    setPromoActiveTab: Function;
    promoConfig: PromoType[]; // property which will contain all the available promo types and tactic types
    promoConfigDictionary: PromoConfigDictionary;
    onGetMetricRecomValuesSuccess: (ppgs: ProductPPG[], triggeredFrom: MetricsApiTriggerPoints) => ProductPPG[];
    isValid: boolean;
    setIsValid: Dispatch<SetStateAction<boolean>>;
    planMetaDataLoading: boolean;
    planIdFromUrl: null | string;
    promoLabelMapping: any;
    tempEventProperties: any;
    setTempEventProperties: Function;
    selectedPromoPPGIds: Array<number>;
    setSelectedPromoPPGIds: Function;
    errorMessage: string;
    setErrorMessage: Dispatch<SetStateAction<string>>;
    isTpmTab: boolean;
    rbacLoading: boolean;
    showSyncedPlans: boolean;
    setShowSyncedPlans: Function;
    rbacConditions: RbacType | null;
    approvalPublishEnabled: boolean;
    approvalPublishToggleLoading: boolean;
    resetCurrentState: () => void;
    isCurrentViewEventLibrary: boolean;
    optimizedPlanId: Number | undefined;
    setOptimizedPlanId: Dispatch<SetStateAction<Number | undefined>>;
    showOptimizedPlanCalender: boolean;
    setShowOptimizedPlanCalender: Dispatch<SetStateAction<boolean>>;
    promoConfigLoading: boolean;
    isKpiAbsView: boolean;
    setIsKpiAbsView: Dispatch<SetStateAction<boolean>>;
    setFilterUIDimensionsLoading: Dispatch<SetStateAction<boolean>>;
    filterUIDimensionsLoading: boolean;
    promoPopupErrors: PopUpErrorObject[];
    setPromoPopupErrors: (msgs: PopUpErrorObject[]) => void;
    clearPromoPopupErrors: () => void;
    promoPopupWarnings: PopUpErrorObject[];
    setPromoPopupWarnings: (msgs: PopUpErrorObject[]) => void;
    clearPromoPopupWarnings: () => void;
    resetFilterObject: () => void;
    updateFilterObject: (filterKeys: any[]) => void;
    updatePlansFilterObject: (filterKeys: any[]) => void;
    updateFilterObjectWithPlansSharing: (filterKeys: any[]) => void;
    updatePlansFilterObjectWithToolsSharing: (filterKeys: any[]) => void;
    availableFilters: any[];
    allCheckedAttribute: any;
    setAllCheckedAttribute: Function;
    allSelectedValueForAllAttribute: any;
    setAllSelectedValueForAllAttribute: Function;
    applyFilterObject: any;
    setApplyFilterObject: Function;
    uiDimensionConfigData: any;
    setUiDimensionConfigData: Function;
    plansIndexAllCheckedAttribute: any;
    setPlansIndexAllCheckedAttribute: Function;
    plansIndexAllSelectedValueForAllAttribute: any;
    setPlansIndexAllSelectedValueForAllAttribute: Function;
    plansIndexApplyFilterObject: any;
    setPlansIndexApplyFilterObject: Function;
    backupPlansAllCheckedAttribute: any;
    setBackupPlansAllCheckedAttribute: Function;
    backupPlansAllSelectedValueForAllAttribute: any;
    setBackupPlansAllSelectedValueForAllAttribute: Function;
    backupPlansApplyFilterObject: any;
    setBackupPlansApplyFilterObject: Function;
    planEditMetaData: PlanEditMetaType;
    setPlanEditMetaData: Dispatch<SetStateAction<PlanEditMetaType>>;
    /** Call after successful upsert_event, upsert_promo, delete_event, delete_promo, delete_all_promos — notifies other tabs only. */
    markTpoPlanContentMutated: () => void;
    pollingStatus: OptimizationPollingStatus;
    setPollingStatus: Dispatch<SetStateAction<OptimizationPollingStatus>>;
    isOptimizationInProgress: boolean;
    upliftModelMutation: UseMutationResult<
        any,
        any,
        {
            mechanicType: string;
            ppgs: ProductPPG[] | undefined;
            onUpliftComplete?: (tactic: PromoTactic) => void;
        },
        unknown
    > | null;
    optimizationError: { title: string; message: string } | null;
    setOptimizationError: Dispatch<SetStateAction<{ title: string; message: string } | null>>;
    planRefreshLoading: boolean;
    setPlanRefreshLoading: Dispatch<SetStateAction<boolean>>;
    infeasibilityPollingStatus: InfeasibilityPollingStatus;
    setInfeasibilityPollingStatus: Dispatch<SetStateAction<InfeasibilityPollingStatus>>;
    infeasibilityData: InfeasibilityConstraintGroup[] | null;
    setInfeasibilityData: Dispatch<SetStateAction<InfeasibilityConstraintGroup[] | null>>;
    divisionMetricMapping: DivisionMetricMapping;
    hasCurrentPromoTacticInitialized: boolean;
    setHasCurrentPromoTacticInitialized: Dispatch<SetStateAction<boolean>>;
    skipSRPUpdateRef: MutableRefObject<boolean>;
    finalCalenderStatues: Record<string, string>;
    setFinalCalenderStatues: Dispatch<SetStateAction<Record<string, string>>>;
    merchMDFPromoCount: number;
    setMerchMDFPromoCount: Dispatch<SetStateAction<number>>;
    validatePlanId: string;
    setValidatePlanId: Dispatch<SetStateAction<string>>;
    validatePlanPollingStatus: ValidatePlanPollingStatus;
    setValidatePlanPollingStatus: Dispatch<SetStateAction<ValidatePlanPollingStatus>>;
    refetchTpmStatus: () => void;
}>({
    viewMode: false,
    isCurrentPromoFormDisabled: false,
    activeTab: null,
    tabs: null,
    setActiveTab: () => {},
    tpmTpoActiveTab: null,
    setTpmTpoActiveTab: () => {},
    breadcrumbItems: [],
    dimensionConfigData: null,
    setDimensionConfigData: () => {},
    kpiConfigData: null,
    kpiRawData: null,
    playbookKpiConfigData: null,
    configDataExists: false,
    kpiConfigLoading: false,
    selectedPlanInfo: null,
    setSelectedPlanInfo: () => {},
    eventViewType: 'calender',
    setEventViewType: () => {},
    planSummarySubTab: 'ps_product_view',
    setPlanSummarySubTab: () => {},
    enableBumpReport: true,
    setEnableBumpReport: () => {},
    showCalenderChanges: true,
    setShowCalenderChanges: () => {},
    showChanges: true,
    setShowChanges: () => {},
    isToolsViewAbsolute: false,
    setIsToolsViewAbsolute: () => {},
    selectedEventInfo: null,
    setSelectedEventInfo: () => {},
    selectedEventLibraryEventInfo: null,
    setSelectedEventLibraryEventInfo: () => {},
    updateSelectedEventProperty: () => {},
    updatePlanProperty: () => {},
    promoActiveTab: NEW_EVENT_PROMO_ID,
    setPromoActiveTab: () => {},
    promoConfig: [],
    promoConfigDictionary: {
        promotions: {},
        promoMetrics: {},
        spendMetrics: {},
        tactics: {},
        kpiDependencies: {},
        kpiFormulas: {},
        promoMetricMapping: {},
    },
    onGetMetricRecomValuesSuccess: () => [],
    isValid: false,
    setIsValid: () => {},
    planMetaDataLoading: false,
    planIdFromUrl: null,
    promoLabelMapping: {},
    tempEventProperties: undefined,
    setTempEventProperties: () => {},
    selectedPromoPPGIds: [],
    setSelectedPromoPPGIds: () => {},
    errorMessage: '',
    setErrorMessage: () => {},
    isTpmTab: false,
    rbacLoading: false,
    rbacConditions: null,
    approvalPublishEnabled: false,
    approvalPublishToggleLoading: false,
    resetCurrentState: () => {},
    isCurrentViewEventLibrary: false,
    showSyncedPlans: false,
    setShowSyncedPlans: () => {},
    optimizedPlanId: undefined,
    setOptimizedPlanId: () => {},
    showOptimizedPlanCalender: false,
    setShowOptimizedPlanCalender: () => {},
    promoConfigLoading: false,
    isKpiAbsView: false,
    setIsKpiAbsView: () => {},
    promoPopupErrors: [],
    setPromoPopupErrors: () => {},
    clearPromoPopupErrors: () => {},
    promoPopupWarnings: [],
    setPromoPopupWarnings: () => {},
    clearPromoPopupWarnings: () => {},
    resetFilterObject: () => {},
    updateFilterObject: () => {},
    updatePlansFilterObject: () => {},
    updateFilterObjectWithPlansSharing: () => {},
    updatePlansFilterObjectWithToolsSharing: () => {},
    availableFilters: [],
    allCheckedAttribute: {},
    setAllCheckedAttribute: () => {},
    allSelectedValueForAllAttribute: {},
    setAllSelectedValueForAllAttribute: () => {},
    applyFilterObject: {},
    setApplyFilterObject: () => {},
    uiDimensionConfigData: null,
    setUiDimensionConfigData: () => {},
    plansIndexAllCheckedAttribute: {},
    setPlansIndexAllCheckedAttribute: () => {},
    plansIndexAllSelectedValueForAllAttribute: {},
    setPlansIndexAllSelectedValueForAllAttribute: () => {},
    plansIndexApplyFilterObject: {},
    setPlansIndexApplyFilterObject: () => {},
    backupPlansAllCheckedAttribute: {},
    setBackupPlansAllCheckedAttribute: () => {},
    backupPlansAllSelectedValueForAllAttribute: {},
    setBackupPlansAllSelectedValueForAllAttribute: () => {},
    backupPlansApplyFilterObject: {},
    setBackupPlansApplyFilterObject: () => {},
    planEditMetaData: initialPlanEditMetaData,
    setPlanEditMetaData: () => {},
    markTpoPlanContentMutated: () => {},
    setPollingStatus: () => {},
    pollingStatus: OptimizationPollingStatus.NULL,
    isOptimizationInProgress: false,
    upliftModelMutation: null,
    optimizationError: null,
    setOptimizationError: () => {},
    planRefreshLoading: false,
    setPlanRefreshLoading: () => {},
    infeasibilityPollingStatus: InfeasibilityPollingStatus.IN_PROGRESS,
    setInfeasibilityPollingStatus: () => {},
    infeasibilityData: null,
    setInfeasibilityData: () => {},
    filterUIDimensionsLoading: false,
    setFilterUIDimensionsLoading: () => {},
    divisionMetricMapping: {},
    hasCurrentPromoTacticInitialized: false,
    setHasCurrentPromoTacticInitialized: () => {},
    skipSRPUpdateRef: { current: false },
    finalCalenderStatues: {},
    setFinalCalenderStatues: () => {},
    merchMDFPromoCount: 0,
    setMerchMDFPromoCount: () => {},
    validatePlanId: '',
    setValidatePlanId: () => {},
    validatePlanPollingStatus: ValidatePlanPollingStatus.NOT_STARTED,
    setValidatePlanPollingStatus: () => {},
    refetchTpmStatus: () => {},
});

interface props {
    children: React.ReactNode;
}

/**
 * Isolated component that owns the `tpm_refresh_status` polling. We keep it in
 * its own component (rendered inside the provider) so that the internal state
 * updates fired by `useCommonApiHandler` on every poll re-render only this
 * component — not the TPO provider — and therefore don't force every
 * `TPODataContext` consumer to re-render every 7 seconds.
 */
const TpmRefreshStatusPoller: FC<{
    onStatusChange: (isRunning: boolean, message: string) => void;
}> = ({ onStatusChange }) => {
    const routerLocation = useLocation();

    useCommonApiHandler({
        method: 'get',
        // Including `pathname` in the queryKey ensures the API is re-triggered on every route change.
        queryKey: ['tpm_refresh_status', routerLocation.pathname],
        url: tradePromoOptimizationUrls.getTpmRefreshStatusUrl,
        queryConfig: {
            refetchInterval: TPM_REFRESH_STATUS_POLL_INTERVAL_MS,
        },
        parserFunction: (data) => {
            onStatusChange(Boolean(data?.tpm_refresh_running), data?.message ?? '');
            return {};
        },
    });

    return null;
};

const TPOContext: FC<props> = ({ children }) => {
    const { appConfig } = useContext(UserContext);
    const screenDetailsWithRoutes = getConfig('screenDetailsWithRoutes', appConfig);
    const [promoActiveTab, setPromoActiveTab] = useState<string | null>(null);
    const tpoConfig = getConfig('tradePromoOptimization', appConfig);
    const enableEditMode = tpoConfig?.enableEditMode;
    const [promoConfig, setPromoConfig] = useState<PromoType[]>([]);
    const [divisionMetricMapping, setDivisionMetricMapping] = useState<DivisionMetricMapping>({});
    const [promoConfigDictionary, setPromoConfigDictionary] = useState<PromoConfigDictionary>({
        promotions: {},
        promoMetrics: {},
        spendMetrics: {},
        tactics: {},
        kpiDependencies: {},
        kpiFormulas: {},
        promoMetricMapping: {},
    });
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [hasCurrentPromoTacticInitialized, setHasCurrentPromoTacticInitialized] = useState(false);
    const skipSRPUpdateRef = useRef(false);

    const [selectedEventInfo, setSelectedEventInfo] = useState<PromoEvent>(resetEventInfo());
    const [selectedEventLibraryEventInfo, setSelectedEventLibraryEventInfo] = useState<EventLibraryEvent | null>(null);
    const [selectedPromoPPGIds, setSelectedPromoPPGIds] = useState<number[]>([]);
    const [tempEventProperties, setTempEventProperties] = useState<any>({});
    const [showSyncedPlans, setShowSyncedPlans] = useState(false);
    const [optimizedPlanId, setOptimizedPlanId] = useState<Number>();
    const [pollingStatus, setPollingStatus] = useState<OptimizationPollingStatus>(OptimizationPollingStatus.NULL);
    const [optimizationError, setOptimizationError] = useState<{ title: string; message: string } | null>(null);
    const [planRefreshLoading, setPlanRefreshLoading] = useState<boolean>(false);
    const [infeasibilityPollingStatus, setInfeasibilityPollingStatus] = useState<InfeasibilityPollingStatus>(
        InfeasibilityPollingStatus.IN_PROGRESS,
    );
    const [infeasibilityData, setInfeasibilityData] = useState<InfeasibilityConstraintGroup[] | null>(null);
    const [validatePlanId, setValidatePlanId] = useState<string>('');
    const [validatePlanPollingStatus, setValidatePlanPollingStatus] = useState<ValidatePlanPollingStatus>(
        ValidatePlanPollingStatus.NOT_STARTED,
    );

    const [showOptimizedPlanCalender, setShowOptimizedPlanCalender] = useState(false);
    const [uiDimensionConfigData, setUiDimensionConfigData] = useState<any>(null);
    const [promoPopupErrors, setPromoPopupErrors] = useState<PopUpErrorObject[]>([]);
    const [promoPopupWarnings, setPromoPopupWarnings] = useState<PopUpErrorObject[]>([]);
    const [availableFilters, setAvailableFilters] = useState<DimensionDropdownType[]>([]);

    // CURRENT TAB FILTER STATES (immediate UI state)
    const [allCheckedAttribute, setAllCheckedAttribute] = useState<any>({}); // Currently selected filter values (Sets) for active tab
    const [allSelectedValueForAllAttribute, setAllSelectedValueForAllAttribute] = useState<any>({}); // "Select All" checkbox states for active tab
    const [applyFilterObject, setApplyFilterObject] = useState<any>({}); // Applied filter configurations for active tab

    // CROSS-TAB PERSISTENT STORAGE (in-memory persistence across tab switches)
    // These "final" objects act as a cross-tab storage mechanism to preserve filter states
    // when switching between Mechanic Constraint, Product Constraint, and Promo Library tabs
    const [finalAllCheckedAttribute, setFinalAllCheckedAttribute] = useState<any>({}); // Persistent storage for selected filter values across all tabs
    const [finalAllSelectedValueForAllAttribute, setFinalAllSelectedValueForAllAttribute] = useState<any>({}); // Persistent storage for "Select All" states across all tabs
    const [finalApplyFilterObject, setFinalApplyFilterObject] = useState<any>({}); // Persistent storage for applied filter configurations across all tabs
    const [finalCalenderStatues, setFinalCalenderStatues] = useState<Record<string, string>>({}); // Persistent storage for calender statuses across all tabs

    // PLANS-SPECIFIC FILTER STATES (separate from other tabs)
    const [plansIndexAllCheckedAttribute, setPlansIndexAllCheckedAttribute] = useState<any>({}); // Plans-specific selected filter values
    const [plansIndexAllSelectedValueForAllAttribute, setPlansIndexAllSelectedValueForAllAttribute] = useState<any>({}); // Plans-specific "Select All" states
    const [plansIndexApplyFilterObject, setPlansIndexApplyFilterObject] = useState<any>({}); // Plans-specific applied filter configurations

    // PLANS-SPECIFIC PERSISTENT STORAGE (for cross-tab persistence within Plans)
    const [backupPlansAllCheckedAttribute, setBackupPlansAllCheckedAttribute] = useState<any>({});
    const [backupPlansAllSelectedValueForAllAttribute, setBackupPlansAllSelectedValueForAllAttribute] = useState<any>(
        {},
    );
    const [backupPlansApplyFilterObject, setBackupPlansApplyFilterObject] = useState<any>({});
    const [merchMDFPromoCount, setMerchMDFPromoCount] = useState(0);

    const navigate = useNavigate();
    //plans page url fetching from config
    const plansPageScreenUrl = useMemo(() => {
        const tpoIndexPage = screenDetailsWithRoutes?.find((item: any) => item?.id === 'tpo');
        const plansIndexPageRoute = tpoIndexPage?.subRoutes?.find((subRoutes: any) => {
            return subRoutes?.pageId === 'plan';
        });

        return `/${tpoIndexPage?.path}/${plansIndexPageRoute?.suffix}`;
    }, [screenDetailsWithRoutes]);

    //tools index page url
    const toolsPageScreenUrl = useMemo(() => {
        const tpoIndexPage = screenDetailsWithRoutes?.find((item: any) => item?.id === 'tpo');
        const toolsIndexPageRoute = tpoIndexPage?.subRoutes?.find((subRoutes: any) => {
            return subRoutes?.pageId === 'promoLibraryTool';
        });

        return `/${tpoIndexPage?.path}/${toolsIndexPageRoute?.suffix}`;
    }, [screenDetailsWithRoutes]);

    //reports index page url
    const reportsPageScreenUrl = useMemo(() => {
        const tpoIndexPage = screenDetailsWithRoutes?.find((item: any) => item?.id === 'tpo');
        const reportsIndexPageRoute = tpoIndexPage?.subRoutes?.find((subRoutes: any) => {
            return subRoutes?.pageId === 'report';
        });

        return `/${tpoIndexPage?.path}/${reportsIndexPageRoute?.suffix}`;
    }, [screenDetailsWithRoutes]);

    //tabs
    const tpoTabs: tabsType[] = [
        {
            key: 'plans',
            label: 'Plans',
            url: plansPageScreenUrl,
            headerLabel: 'Plans',
            component: <Plans />,
        },
        {
            key: 'tools',
            label: 'Tools',
            url: toolsPageScreenUrl,
            headerLabel: 'Tools',
            component: <Tools />,
        },
        {
            key: 'reports',
            label: 'Reports',
            url: reportsPageScreenUrl,
            headerLabel: 'Reports',
            component: <Reports />,
        },
    ];

    const [activeTab, setActiveTab] = useState<null | tabsType>(getTabFromUrl(tpoTabs));
    const [tpmTpoActiveTab, setTpmTpoActiveTab] = useState<null | tabsType>(null);

    //stores all the dimension config values
    const [dimensionConfigData, setDimensionConfigData] = useState<null | DimensionConfigType>(null);
    const [kpiConfigData, setKpiConfigData] = useState<null | KpiConfigType>(null);
    const [playbookKpiConfigData, setPlaybookKpiConfigData] = useState<null | PlaybookKpiConfigType>(null);

    //selected plan info
    const [selectedPlanInfo, setSelectedPlanInfo] = useState<null | SelectedPlanInfoType>(null);
    //plan edit meta info
    const [planEditMetaData, setPlanEditMetaData] = useState<PlanEditMetaType>({ ...initialPlanEditMetaData });
    //view dropdown state
    const [eventViewType, setEventViewType] = useState<EventViewType>('calender');
    const [planSummarySubTab, setPlanSummarySubTab] = useState<PlanSummarySubTab>('ps_product_view');
    const [enableBumpReport, setEnableBumpReport] = useState(false);
    const [showCalenderChanges, setShowCalenderChanges] = useState(false);
    const [showChanges, setShowChanges] = useState(false);
    const [isToolsViewAbsolute, setIsToolsViewAbsolute] = useState(true);
    const [isValid, setIsValid] = useState(false);
    const [isKpiAbsView, setIsKpiAbsView] = useState(false);
    const [rbacConditions, setRbacConditions] = useState<null | RbacType>(null);
    const [filterUIDimensionsLoading, setFilterUIDimensionsLoading] = useState(false);
    const configDataExists = Boolean(
        dimensionConfigData?.dim1Attribute?.key || dimensionConfigData?.dim2Attribute?.key,
    );
    const divisionSpendMetrics = divisionMetricMapping?.[Number(selectedPlanInfo?.division)]?.spends;
    const divisionPromoMetrics = divisionMetricMapping?.[Number(selectedPlanInfo?.division)]?.metrics;
    const { mode, planId } = useParams(); //planId from url will always be a string

    // Cross-tab refresh prompt: mutating tab writes localStorage only; other tabs get `storage` + optional visibility follow-up.
    const tpoPlanMutatedForRefreshPromptRef = useRef(false);
    const tpoPlanTabWasHiddenRef = useRef(false);
    const staleRefreshPromptOpenRef = useRef(false);

    const markTpoPlanContentMutated = useCallback(() => {
        if (!planId) return;
        localStorage.setItem(getPlanStaleBroadcastKey(planId), String(Date.now()));
    }, [planId]);

    const openPlanStaleRefreshModal = useCallback(() => {
        if (staleRefreshPromptOpenRef.current) return;
        if (!tpoPlanMutatedForRefreshPromptRef.current) return;

        staleRefreshPromptOpenRef.current = true;
        Modal.confirm({
            title: 'Refresh recommended',
            content: 'Plan data was updated in another tab. Refresh to load the latest data.',
            okText: 'Refresh',
            okCancel: false,
            closable: false,
            maskClosable: false,
            keyboard: false,
            centered: true,
            onOk: () => {
                staleRefreshPromptOpenRef.current = false;
                window.location.reload();
            },
        });
    }, []);

    useEffect(() => {
        if (!planId) return;
        const staleKey = getPlanStaleBroadcastKey(planId);

        const handleStorage = (e: StorageEvent) => {
            if (e.key !== staleKey || e.newValue == null) return;
            tpoPlanMutatedForRefreshPromptRef.current = true;
            if (document.visibilityState === 'visible') {
                openPlanStaleRefreshModal();
            }
        };

        const handleVisibility = () => {
            if (document.visibilityState === 'hidden') {
                tpoPlanTabWasHiddenRef.current = true;
                return;
            }
            if (!tpoPlanMutatedForRefreshPromptRef.current || !tpoPlanTabWasHiddenRef.current) return;
            tpoPlanTabWasHiddenRef.current = false;
            openPlanStaleRefreshModal();
        };

        window.addEventListener('storage', handleStorage);
        document.addEventListener('visibilitychange', handleVisibility);
        return () => {
            window.removeEventListener('storage', handleStorage);
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, [planId, openPlanStaleRefreshModal]);

    // const isViewMode = () => mode === 'view';
    const isViewMode = enableEditMode ? () => mode === 'view' : () => false;

    //const isUpdateMode = () => mode === 'update';
    const isUpdateMode = enableEditMode ? () => mode === 'update' : () => true;

    //hook to check if visibility of tab has changed
    const isTabChanged = useTrackTabChange();

    const isCurrentPromoDisabled = (): boolean => {
        const tactic = selectedEventInfo?.currentPromoTactic;
        if (!tactic?.ppgs?.length) return false;

        const promoStartDate = tactic.promoStartDate ?? Infinity;
        const utcConvertedDate = dayjs.unix(promoStartDate).utc(); // Ensure UTC interpretation

        // const isPastPromo = utcConvertedDate.isBefore(dayjs().utc().startOf('day'));

        const isExecuted = tactic.status === 'executed';
        // return isPastPromo || isExecuted;
        return isExecuted;
    };

    //trigger meta data api call on
    useEffect(() => {
        if (isTabChanged) {
            queryClient.removeQueries({
                queryKey: ['tpo_plan_meta_data', 'tpo_plan_edit_meta_data', planId],
                exact: true,
            });
        }
    }, [isTabChanged]);

    useEffect(() => {
        //load the config
        const {
            dim1Attribute,
            dim2Attribute,
            eventDim1Attribute,
            eventDim1NestedAttribute,
            dimensionDropdownList,
            planViewDropdownList,
            calendarColumnDropdownList,
        } = tpoConfig;
        const configData = {
            dim1Attribute,
            dim2Attribute,
            eventDim1Attribute,
            eventDim1NestedAttribute,
            dimensionDropdownList:
                eventViewType === 'plan_metrics'
                    ? planViewDropdownList
                    : eventViewType === 'promo_metrics'
                    ? dimensionDropdownList
                    : [],
            calendarColumnDropdownList: calendarColumnDropdownList || [],
        };
        setDimensionConfigData(configData);
    }, [appConfig, eventViewType]);

    useEffect(() => {
        if (errorMessage) {
            setTimeout(() => {
                setErrorMessage('');
            }, 5000);
        }
    }, [errorMessage]);

    //reset event info
    function resetEventInfo(): PromoEvent {
        return {
            name: 'Promo Event 1',
            createdBy: 'asd',
            // offerMethod: 'previous',
            // offer: 's',
            displayACV: 12,
            featureACV: 122,
            selectedDim1Attributes: ['brand-1', 'brand-3'],
            selectedDim1NestedAttributes: ['ppg-1', 'ppg-3'],
            CRAStartDate: Date.now() - 7 * 24 * 60 * 60 * 1000, // 7 days ago
            CRAEndDate: Date.now() + 7 * 24 * 60 * 60 * 1000, // 7 days from now
            promoTacticsIdNamePair: [{ id: '1', name: 'Promo 1', promotionType: null }],
            currentPromoTactic: {
                isLoading: false,
                name: 'Promo 1',
                tpmPromoId: null,
                status: null,
                customPromo: null,
                promoId: '1',
                tacticType: 0,
                promoType: 0,
                promoStartDate: null,
                promoEndDate: null,
                belowLUC: false,
                belowMPIC: false,
                ppgs: [],
                featureQuality: null,
                displayQuality: null,
                offerType: null,
                manualOffer: null,
                buyGet: null,
                buyFor: null,
                buySave: null,
                discount: null,
                offer: null,
                craEndDate: null,
                craStartDate: null,
                discountUnit: 'abs',
                comboUnit: 'abs',
                buySaveUnit: 'abs',
                buyForUnit: 'abs',
            },
        };
    }

    //redirect users from /tpo to /tpo/plan by default
    useEffect(() => {
        const tpoIndexPage = screenDetailsWithRoutes?.find((item: any) => item?.id === 'tpo');
        if (location.pathname === `/${tpoIndexPage?.path}`) {
            //navigate to plans page
            navigate(plansPageScreenUrl);
        }
    }, [location.pathname, plansPageScreenUrl]);

    // ---------------------------------------------------------------------
    // TPM refresh status polling
    // ---------------------------------------------------------------------
    // The actual polling lives in <TpmRefreshStatusPoller /> below so that its
    // internal `useCommonApiHandler` re-renders (it calls `setData({})` after
    // every poll — a fresh object reference) don't bubble up to this provider
    // and force every TPODataContext consumer to re-render every 7s.
    const routerLocation = useLocation();
    // Tracks the last seen running flag so we only fire notifications on actual
    // running <-> not-running transitions.
    const tpmRefreshRunningRef = useRef<boolean>(false);
    // Drives the full-screen "TPM refresh in progress" overlay and forces a redirect
    // to the Plans page while a refresh is happening.
    const [tpmRefreshRunning, setTpmRefreshRunning] = useState<boolean>(false);
    const [tpmRefreshMessage, setTpmRefreshMessage] = useState<string>('');

    // Stable callback invoked by the poller component after every poll. Since
    // `setState` bails out when the next value is `Object.is`-equal to the
    // current one, this only re-renders the provider when the running flag or
    // the message actually changes.
    const handleTpmStatusChange = useCallback((isRunning: boolean, message: string) => {
        const previous = tpmRefreshRunningRef.current;
        if (previous !== isRunning && message) {
            sendNotification(isRunning ? 'info' : 'success', message);
        }
        tpmRefreshRunningRef.current = isRunning;
        setTpmRefreshRunning(isRunning);
        setTpmRefreshMessage(message);
    }, []);

    // While a TPM refresh is running, force the user back to the Plans page from
    // any other TPO route (Tools, Reports, plan detail / event library, etc.).
    // Also reset `activeTab` to Plans — TPOPage renders `activeTab?.component`,
    // so navigating without updating activeTab would leave the wrong tab visible
    // once the refresh overlay disappears.
    useEffect(() => {
        if (tpmRefreshRunning && plansPageScreenUrl && routerLocation.pathname !== plansPageScreenUrl) {
            const plansTab = tpoTabs.find((t) => t.key === 'plans');
            if (plansTab) setActiveTab(plansTab);
            navigate(plansPageScreenUrl);
        }
    }, [tpmRefreshRunning, plansPageScreenUrl, routerLocation.pathname]);

    // Drops every cached `tpm_refresh_status` entry.
    const refetchTpmStatus = useCallback(() => {
        queryClient.removeQueries({ queryKey: ['tpm_refresh_status'], exact: false });
    }, []);

    useEffect(() => {
        return () => {
            refetchTpmStatus();
        };
    }, [routerLocation.pathname, activeTab?.key, tpmTpoActiveTab?.key, refetchTpmStatus]);

    //mapping promo type label for legends
    const promoLabelMapping = useMemo(() => {
        if (promoConfig && promoConfig?.length > 0) {
            const result = promoConfig.reduce((acc: any, promotion, index: number) => {
                acc[promotion?.promotion_id] = {
                    label: promotion.promotion_name,
                    color: tpoConfig?.legendColorsArray?.[index],
                };
                return acc;
            }, {});
            return result;
        }
        return {};
    }, [promoConfig]);

    //breadcrumb
    const breadcrumbItems: BreadcrumbItem[] = useMemo(() => {
        const defaultBreadCrumbItem = { label: 'Trade Promo Optimization' };
        const indexPageBreadCrumbLabel = activeTab?.headerLabel ?? '';
        //if in index page, show breadcrumbs for index page
        if ([plansPageScreenUrl, toolsPageScreenUrl, reportsPageScreenUrl].includes(location.pathname)) {
            return [defaultBreadCrumbItem, { label: indexPageBreadCrumbLabel }];
        }

        if (isViewMode() || isUpdateMode()) {
            let breadcrumbsItems = [defaultBreadCrumbItem, { label: indexPageBreadCrumbLabel, url: activeTab?.url }];
            //load project name to breadcrumbs once its available
            if (selectedPlanInfo?.planName) {
                breadcrumbsItems = [...breadcrumbsItems, { label: selectedPlanInfo?.planName }];
            }
            return breadcrumbsItems;
        }

        return [];
    }, [location.pathname, activeTab?.key, selectedPlanInfo?.planName]);

    function updatePlanProperty<K extends keyof SelectedPlanInfoType>(key: K, value: SelectedPlanInfoType[K]) {
        setSelectedPlanInfo((prevState: any) => {
            const updatedPlan = {
                ...(prevState || {}),
                [key]: value,
            };
            return updatedPlan;
        });
        // const updatedScenario = {
        //     ...selectedScenarioInfo,
        //     [key]: value,
        // };
        // // might require to call the API here
        // setSelectedScenarioInfo(updatedScenario);
    }
    //function to update the url to view and update based on edit rights
    const toggleModeFn = (toggleMode: 'update' | 'view') => {
        if (toggleMode === 'update') {
            const updatedPath = location.pathname.replace('/view/', '/update/');
            navigate(updatedPath);
        } else if (toggleMode === 'view') {
            const viewPath = location.pathname.replace('/update/', '/view/');
            navigate(viewPath);
        }
    };

    //if the user has editing rights, then change the url to update else view
    useEffect(() => {
        if (planEditMetaData?.editingUser && compareUsers(planEditMetaData?.editingUser)) {
            toggleModeFn('update');
        } else {
            toggleModeFn('view');
        }
    }, [planEditMetaData]);

    //clean up function when plan id changes
    useEffect(() => {
        return () => {
            tpoPlanMutatedForRefreshPromptRef.current = false;
            tpoPlanTabWasHiddenRef.current = false;
            staleRefreshPromptOpenRef.current = false;
            setSelectedPlanInfo(null);
            setSelectedEventInfo(resetEventInfo());
            setShowCalenderChanges(false);
            setTpmTpoActiveTab(null);
            setEventViewType('calender');
            setOptimizedPlanId(undefined);
            setShowOptimizedPlanCalender(false);
            setPlanEditMetaData({ ...initialPlanEditMetaData });
            setPollingStatus(OptimizationPollingStatus.NULL);
            setInfeasibilityPollingStatus(InfeasibilityPollingStatus.IN_PROGRESS);
            setInfeasibilityData(null);
            setFinalCalenderStatues({});
            setValidatePlanId('');
            setValidatePlanPollingStatus(ValidatePlanPollingStatus.NOT_STARTED);
            //remove plan meta data and plan edit meta data on plan id change
            queryClient.removeQueries({
                queryKey: ['tpo_plan_meta_data'],
                exact: false,
            });
        };
    }, [planId]);
    //remove config data on route change to other features
    useEffect(() => {
        return () => {
            queryClient.removeQueries({
                queryKey: ['tpo-playbook-kpi-config'],
            });
            queryClient.removeQueries({
                queryKey: ['tpo_promo_config'],
            });
            queryClient.removeQueries({
                queryKey: ['tpo-kpi-config'],
            });
            queryClient.removeQueries({
                queryKey: ['tpo-promo-library-kpi-config'],
            });
        };
    }, []);

    //if its false, it means the current view is plan view - there can be 2 views: plans and event library
    const isCurrentViewEventLibrary = useMemo(() => {
        return location.pathname?.includes('event-library');
    }, [location.pathname]);

    //this variable tells if config is loaded or not
    const isConfigLoaded = Boolean(appConfig && appConfig?.commonConfig);

    //GET KPI CONFIG DATA FROM BACKEND
    const { loading: kpiConfigLoading, data: kpiRawData } = useFetchKpiConfigData({
        method: 'get',
        queryConfig: { enabled: isConfigLoaded },
        queryKey: ['tpo-kpi-config'],
        url: `${tradePromoOptimizationUrls.getKpiConfigDataUrl}`,
        variables: {},
        setKpiConfigData,
    });

    const { loading: promoConfigLoading } = useCommonApiHandler({
        method: 'get',
        queryKey: ['tpo_promo_config'],
        url: `${tradePromoOptimizationUrls.getPromoMetricsConfigUrl}`,
        variables: {},
        queryConfig: { enabled: isConfigLoaded },
        parserFunction: (data) => {
            setPromoConfig(data?.data || []);
            setDivisionMetricMapping(data?.division_metric_mapping);
            const result = getPromoConfigDataParser(data?.data || []);
            setPromoConfigDictionary(result);
            return {};
        },
    });

    //rbac url
    const { loading: rbacLoading, error: rbacError } = useCommonApiHandler({
        method: 'post',
        queryKey: ['tpo_rbac_data'],
        url: `${tradePromoOptimizationUrls.getRbacDataUrl}`,
        variables: {
            feature_name: 'tpo',
        },
        queryConfig: { enabled: configDataExists },
        parserFunction: (data) => {
            if (!data) throw 'No data';
            setRbacConditions({
                showCreatePlanButton: Boolean(data?.sub_features?.includes('create_tpo_plan')),
                showApproveAndPublish: Boolean(data?.sub_features?.includes('tpo_plan_approval_and_publish')),
                showApprovalPublishControl: Boolean(
                    data?.sub_features?.includes('tpo_plan_approval_and_publish_control'),
                ),
            });

            return {};
        },
    });

    //approval & publish feature toggle state. Fetched for every user, not just
    //those allowed to flip it, since it gates the approval/publish actions for all.
    //useCommonApiHandler can't be used here: it runs `'error' in data`, which
    //throws when the endpoint responds with a bare boolean.
    const { data: approvalPublishToggleData, isFetching: approvalPublishToggleLoading } = useQuery({
        queryKey: [TPO_APPROVAL_PUBLISH_TOGGLE_QUERY_KEY],
        enabled: configDataExists,
        queryFn: async () => {
            const response = await axios.get(tradePromoOptimizationUrls.featureToggleUrl, {
                params: { feature_name: TPO_APPROVAL_PUBLISH_FEATURE_NAME },
            });
            return Boolean(response?.data);
        },
    });
    const approvalPublishEnabled = approvalPublishToggleData ?? false;

    //plan meta api
    const { loading: planMetaDataLoading, error: planMetaDataError } = useCommonApiHandler({
        method: 'get',
        queryConfig: { enabled: Boolean(planId && isConfigLoaded) },
        queryKey: ['tpo_plan_meta_data', planId],
        url: `${tradePromoOptimizationUrls.getPlanMetaDataUrl}/${planId}`,
        parserFunction: (data) => {
            if (!data) throw 'No data';
            if (data?.is_tpm_plan) setTpmTpoActiveTab(tpmTpoTabComponent[1]);
            setSelectedPlanInfo({
                createdBy: data?.created_by,
                planId: data?.plan_id,
                planName: data?.plan_name,
                planYear: data?.plan_year,
                status: data?.status,
                isTpmPlan: data?.is_tpm_plan,
                updatedAt: dayjs.unix(data?.updated_at).fromNow(),
                division: data?.division,
                divisionName: data?.division_name,
                dim1Filter: { label: data?.dim1_attribute_type, value: data?.dim1_attribute_value },
                dim2Filter: { label: data?.dim2_attribute_type, value: [data?.dim2_attribute_value] },
                enableOptimization: data?.enable_optimization,
                tpm_last_sync_date: data?.tpm_last_sync_date,
                customerType: data?.customer_type,
                baselineVolumeVersion: data?.baseline_volume_version,
            });
            return {};
        },
    });

    //plan meta edit api
    const { loading: planMetaEditDataLoading } = useCommonApiHandler({
        method: 'get',
        queryConfig: { enabled: Boolean(planId && isConfigLoaded) },
        queryKey: ['tpo_plan_meta_data', 'tpo_plan_edit_meta_data', planId],
        url: `${tradePromoOptimizationUrls.getPlanEditMetaDataUrl}/${planId}/edit/meta`,
        parserFunction: (data) => {
            setPlanEditMetaData({
                editingUser: data?.edit_lock?.editing_user,
                sessionExpiresInSecs: data?.edit_lock?.session_expires_in_sec,
                startedAt: data?.edit_lock?.started_at,
                planId: data?.plan_id,
            });
            return {};
        },
    });

    function updateSelectedEventProperty<K extends keyof PromoEvent>(key: K, value: PromoEvent[K]) {
        setSelectedEventInfo((prevState: any) => {
            const updatedSelectedEvent = {
                ...(prevState || {}),
                [key]: value,
            };
            return updatedSelectedEvent;
        });
    }

    //if error occurs in plan meta api, then redirect to plan landing page
    useEffect(() => {
        if (planMetaDataError) {
            navigate(plansPageScreenUrl);
        }
    }, [planMetaDataError]);

    // Stable identity + no-op when already empty: Promos.tsx effects depend on these
    // callbacks. Recreating them (or always setState([])) re-rendered context on every
    // tick and reset KPISelector Material Group temp selection mid-interaction.
    const clearPromoPopupErrors = useCallback(() => {
        setPromoPopupErrors((prev) => (prev.length === 0 ? prev : []));
    }, []);

    const clearPromoPopupWarnings = useCallback(() => {
        setPromoPopupWarnings((prev) => (prev.length === 0 ? prev : []));
    }, []);

    // Helper function to determine if a metric should be hidden based on division metrics
    const isMetricHiddenByDivision = (metricId: number, divisionMetrics?: Number[]): boolean => {
        return !divisionMetrics?.includes(metricId);
    };

    // Helper function to determine if a metric should be hidden based on customer type
    const isMetricHiddenByCustomerType = (
        metric: Metric,
        defaultMetrics?: Record<number, ItemWithEditable>,
        selectedPlanInfo?: SelectedPlanInfoType | null,
    ): boolean => {
        const metricConfig = defaultMetrics?.[metric.id];
        if (!metricConfig?.customerType) return false;

        const customerType = selectedPlanInfo?.customerType?.toLowerCase();
        return !(metricConfig.customerType === 'both' || metricConfig.customerType === customerType);
    };

    // Helper function to get metric name with fallback logic
    const getMetricName = (
        metric: Metric,
        defaultMetrics?: Record<number, ItemWithEditable>,
        selectedMetrics?: Record<number, Metric>,
    ): string => {
        return defaultMetrics?.[metric.id]?.name ?? selectedMetrics?.[metric.id]?.name ?? '';
    };

    // Helper function to determine if metric should be selected
    const shouldMetricBeSelected = (
        metric: Metric,
        defaultMetrics?: Record<number, ItemWithEditable>,
        selectedMetrics?: Record<number, Metric>,
    ): boolean => {
        return defaultMetrics?.[metric.id]?.defaultShow ?? !!selectedMetrics?.[metric.id]?.isSelected;
    };

    // Helper function to determine if metric should be hidden
    const shouldMetricBeHidden = (
        metric: Metric,
        defaultMetrics?: Record<number, ItemWithEditable>,
        selectedMetrics?: Record<number, Metric>,
        divisionMetrics?: Number[],
    ): boolean => {
        return (
            isMetricHiddenByDivision(metric.id, divisionMetrics) ||
            isMetricHiddenByCustomerType(metric, defaultMetrics, selectedPlanInfo) ||
            !!selectedMetrics?.[metric.id]?.isHidden ||
            false
        );
    };

    const getUpdatedIsSelected = (
        array: Metric[],
        selectedMetrics?: Record<number, Metric>,
        defaultMetrics?: Record<number, ItemWithEditable>,
        divisionMetrics?: Number[],
    ): Metric[] =>
        array.map((metric) => ({
            ...metric,
            name: getMetricName(metric, defaultMetrics, selectedMetrics),
            isSelected: shouldMetricBeSelected(metric, defaultMetrics, selectedMetrics),
            isHidden: shouldMetricBeHidden(metric, defaultMetrics, selectedMetrics, divisionMetrics),
        }));

    // get updated metric values for date change from the api
    const getUpdatedMetricValues = (
        array: Metric[],
        defaultMetrics?: Record<number, Metric & { nameKey: string }>,
        selectedMetrics?: Record<number, Metric>,
        metricType: 'metric' | 'spend' = 'metric',
    ): Metric[] => {
        const x = array.map((metric) => ({
            ...metric,
            isSelected: selectedMetrics
                ? selectedMetrics?.[metric.id]?.isSelected
                : defaultMetrics?.[metric.id]?.isSelected || false,
            value: computeMetricValue(metric, defaultMetrics, selectedMetrics, metricType) || '0',
            recomValue: selectedMetrics?.[metric.id]?.recomValue || '0',
        }));
        return x;
    };

    const computeMetricValue = (
        metric: Metric,
        defaultMetrics?: Record<number, Metric & { nameKey: string }>,
        selectedMetrics?: Record<number, Metric>,
        metricType: 'metric' | 'spend' = 'metric',
    ) => {
        const retainableMetrics = [
            tpoPromoMetricKeys.SRP,
            tpoPromoMetricKeys.PERCENT_OF_CONSUMPTION,
            tpoPromoMetricKeys.DISPLAY_ACV_PERCENT,
            tpoPromoMetricKeys.FEATURE_ACV_PERCENT,
            tpoPromoMetricKeys.SRP_TRUE,
            tpoPromoMetricKeys.PROMO_INC_UNITS_OVERRIDE,
            tpoPromoMetricKeys.PROMOTED_SRP,
        ];
        if (
            (metricType === 'spend' && defaultMetrics?.[metric.id]?.isEditable) ||
            (metricType === 'metric' &&
                retainableMetrics.includes(defaultMetrics?.[metric.id]?.nameKey as tpoPromoMetricKeys) &&
                selectedMetrics)
        ) {
            return selectedMetrics?.[metric.id]?.value;
        }
        return defaultMetrics?.[metric.id]?.value || '0';
    };

    // get updated metric values for tactic type change from the api
    const getUpdatedMetricValuesForTacticChange = (array: Metric[], defaultMetrics: Record<number, Metric>): Metric[] =>
        array.map((metric) => ({
            ...metric,
            isSelected: defaultMetrics?.[metric.id].isSelected || false,
            value: defaultMetrics?.[metric.id]?.value || '0',
            recomValue: defaultMetrics?.[metric.id]?.recomValue || '0',
            isHidden: defaultMetrics?.[metric.id]?.isHidden ?? false,
        }));

    const getMetricsObject = (metrics: Metric[], isMetric: boolean) => {
        const avlMetrics = isMetric ? promoConfigDictionary?.promoMetrics : promoConfigDictionary?.spendMetrics;
        return !metrics
            ? {}
            : metrics?.reduce((acc, item) => {
                  acc[item.id] = { ...item, nameKey: avlMetrics?.[item.id]?.name_key };
                  return acc;
              }, {} as Record<number, Metric & { nameKey: string }>);
    };

    const getMetricsDefaultObject = (metrics: Metric[], isMetric: boolean) => {
        const avlMetrics = isMetric
            ? promoConfigDictionary.promoMetricMapping[Number(selectedEventInfo?.currentPromoTactic.promoType)]
                  ?.promoMetrics
            : promoConfigDictionary.promoMetricMapping[Number(selectedEventInfo?.currentPromoTactic.promoType)]
                  ?.spendMetrics;
        const divisionMetrics = isMetric ? divisionPromoMetrics : divisionSpendMetrics;
        return !metrics
            ? {}
            : metrics?.reduce((acc, item) => {
                  acc[item.id] = {
                      ...item,
                      isSelected: avlMetrics[item.id].defaultShow,
                      isEditable: avlMetrics[item.id].editable,
                      isHidden:
                          !divisionMetrics.includes(item.id) ||
                          !(
                              avlMetrics[item.id]?.customerType === 'both' ||
                              avlMetrics[item.id]?.customerType === selectedPlanInfo?.customerType.toLowerCase()
                          ),
                      nameKey: avlMetrics?.[item.id]?.name_key,
                  };
                  return acc;
              }, {} as Record<number, Metric & { nameKey: string }>);
    };

    // On PPGChange, add the already selected metrics to the new PPGs.
    // If it’s the first time a PPG is added, get the selected metrics from defaultSelection.
    const getUpdatedPPgsFromFormChange = (
        responsePPGs: ProductPPG[],
        currentPPGs: ProductPPG[],
        deletedPPGs: Number[],
        selectedMetrics: Record<number, Metric>,
        selectedSpendMetrics: Record<number, Metric>,
    ): ProductPPG[] => {
        if (currentPPGs.length) {
            const newPPgs = [...currentPPGs, ...responsePPGs].reduce((acc: ProductPPG[], ppg) => {
                if (!deletedPPGs.length || !deletedPPGs.includes(ppg.id)) {
                    const { id, metrics, spendDetails } = ppg;
                    // getUpdatedIsSelected is a function that updates the `isSelected` field
                    const newMetrics = getUpdatedIsSelected(metrics, selectedMetrics, undefined, divisionPromoMetrics);
                    const newSpendMetrics = getUpdatedIsSelected(
                        spendDetails,
                        selectedSpendMetrics,
                        undefined,
                        divisionSpendMetrics,
                    );
                    acc.push({
                        ...ppg,
                        isSelected: true,
                        spendDetails: newSpendMetrics,
                        metrics: newMetrics,
                        name: getPPGName(id),
                    });
                }
                return acc;
            }, [] as ProductPPG[]);
            return newPPgs;
        } else {
            return responsePPGs.map((ppg) => {
                const { id, metrics, spendDetails } = ppg;
                return {
                    ...ppg,
                    metrics: getUpdatedIsSelected(
                        metrics,
                        undefined,
                        promoConfigDictionary.promoMetricMapping[
                            Number(selectedEventInfo?.currentPromoTactic.promoType)
                        ]?.promoMetrics,
                        divisionPromoMetrics,
                    ),
                    spendDetails: getUpdatedIsSelected(
                        spendDetails,
                        undefined,
                        promoConfigDictionary.promoMetricMapping[
                            Number(selectedEventInfo?.currentPromoTactic.promoType)
                        ]?.spendMetrics,
                        divisionSpendMetrics,
                    ),
                    isSelected: true,
                    name: getPPGName(id),
                };
            });
        }
    };

    const getUpdatedPPGsFromFormDateChange = (ppgs: ProductPPG[]) => {
        return ppgs.map((ppg) => {
            const { id, metrics, spendDetails } = ppg;

            const defaultSelectedMetrics = getMetricsDefaultObject(metrics, true);
            const defaultSelectedSpendMetrics = getMetricsDefaultObject(spendDetails, false);
            let selectedMetrics = undefined,
                selectedSpendMetrics = undefined;
            const currentPPG = selectedEventInfo?.currentPromoTactic?.ppgs.find((item) => item.id === id);
            if (currentPPG && currentPPG.isSelected) {
                selectedMetrics = getMetricsObject(currentPPG.metrics, true);
                selectedSpendMetrics = getMetricsObject(currentPPG.spendDetails, false);
            }

            return {
                ...ppg,
                isSelected: true,
                name: getPPGName(id),
                metrics: getUpdatedMetricValues(metrics, defaultSelectedMetrics, selectedMetrics),
                // if its just date change we need to retain the same spends details, so using isDateTriggered flag
                spendDetails: getUpdatedMetricValues(
                    spendDetails,
                    defaultSelectedSpendMetrics,
                    selectedSpendMetrics,
                    'spend',
                ),
            };
        });
    };

    //  when update flow retain the selected spends details and metrics else get the default values
    const getUpdatedPPGsFromFormUpdateChange = (ppgs: ProductPPG[]) => {
        if (selectedEventInfo.currentPromoTactic.promoId === NEW_EVENT_PROMO_ID) {
            // add flow
            return ppgs.map((ppg) => {
                const defaultSelectedMetrics = getMetricsDefaultObject(ppg.metrics, true);
                const defaultSelectedSpendMetrics = getMetricsDefaultObject(ppg.spendDetails, false);
                const { id, metrics, spendDetails } = ppg;
                return {
                    ...ppg,
                    isSelected: true,
                    name: getPPGName(id),
                    metrics: getUpdatedMetricValuesForTacticChange(metrics, defaultSelectedMetrics),
                    spendDetails: getUpdatedMetricValuesForTacticChange(spendDetails, defaultSelectedSpendMetrics),
                };
            });
        }

        // update flow
        return ppgs.map((ppg) => {
            const { id, metrics, spendDetails } = ppg;

            const defaultSelectedMetrics = getMetricsDefaultObject(metrics, true);
            const defaultSelectedSpendMetrics = getMetricsDefaultObject(spendDetails, false);
            let selectedMetrics = undefined,
                selectedSpendMetrics = undefined;
            const currentPPG = selectedEventInfo?.currentPromoTactic?.ppgs.find((item) => item.id === id);
            if (currentPPG && currentPPG.isSelected) {
                selectedMetrics = getMetricsObject(currentPPG.metrics, true);
                selectedSpendMetrics = getMetricsObject(currentPPG.spendDetails, false);
            }

            return {
                ...ppg,
                isSelected: true,
                name: getPPGName(id),
                metrics: getUpdatedMetricValues(metrics, defaultSelectedMetrics, selectedMetrics),
                // if its just date change we need to retain the same spends details, so using isDateTriggered flag
                spendDetails: getUpdatedMetricValues(
                    spendDetails,
                    defaultSelectedSpendMetrics,
                    selectedSpendMetrics,
                    'spend',
                ),
            };
        });
    };

    // on change of manual offer if its discount we should update promoted srp
    useEffect(() => {
        if (!selectedEventInfo?.currentPromoTactic || isViewMode()) return;

        const { offerType, manualOffer, ppgs } = selectedEventInfo.currentPromoTactic;

        if (skipSRPUpdateRef.current) {
            // consume the skip flag ONCE which set in Promo / Spend metric component select kpi handler functions
            skipSRPUpdateRef.current = false;
            return;
        }

        if (ppgs?.length > 0 && offerType && manualOffer && hasCurrentPromoTacticInitialized) {
            setPromotedSRPValue(ppgs);
        }
    }, [
        selectedEventInfo?.currentPromoTactic?.offerType,
        selectedEventInfo?.currentPromoTactic?.manualOffer,
        selectedEventInfo?.currentPromoTactic?.discount,
        selectedEventInfo?.currentPromoTactic?.discountUnit,
        selectedEventInfo?.currentPromoTactic?.comboUnit,
        selectedEventInfo?.currentPromoTactic?.buyGet,
        selectedEventInfo?.currentPromoTactic?.buyFor,
        selectedEventInfo?.currentPromoTactic?.buySave,
        selectedEventInfo?.currentPromoTactic?.buySaveUnit,
    ]);

    const setPromotedSRPValue = (ppgs: ProductPPG[]) => {
        const edlpSrpTrue = getPromoMetricByNameKeyName(promoConfigDictionary, tpoPromoMetricKeys.EDLP_SRP_TRUE);
        const promotedSrp = getPromoMetricByNameKeyName(promoConfigDictionary, tpoPromoMetricKeys.PROMOTED_SRP);
        const couponOverlay = getPromoMetricByNameKeyName(
            promoConfigDictionary,
            tpoPromoMetricKeys.COUPON_OR_OVERLAY_VALUE,
        );
        const percentOfConsumption = getPromoMetricByNameKeyName(
            promoConfigDictionary,
            tpoPromoMetricKeys.PERCENT_OF_CONSUMPTION,
        );
        const promotedAur = getPromoMetricByNameKeyName(promoConfigDictionary, tpoPromoMetricKeys.PROMOTED_AUR);

        const {
            discount: rawDiscount = 0,
            discountUnit: rawDiscountUnit = '',
            manualOffer: rawManualOffer = '',
            buyGet: rawBuyGet = [0, 0],
            comboUnit: rawComboUnit = '',
            buyFor: rawBuyFor = [0, 0],
            buySave: rawBuySave = [0, 0],
            buySaveUnit: rawBuySaveUnit = 'abs',
        } = selectedEventInfo?.currentPromoTactic ?? {};

        // Ensure proper types: numbers, strings, and arrays of numbers
        const discount = Number(rawDiscount) || 0;
        const discountUnit = String(rawDiscountUnit || '');
        const manualOffer = String(rawManualOffer || '');
        const buyGet = rawBuyGet ? (rawBuyGet as number[]).map(Number) : [0, 0];
        const comboUnit = String(rawComboUnit || '');
        const buyFor = rawBuyFor ? (rawBuyFor as number[]).map(Number) : [0, 0];
        const buySave = rawBuySave ? (rawBuySave as number[]).map(Number) : [0, 0];
        const buySaveUnit = String(rawBuySaveUnit || 'abs');

        if (!edlpSrpTrue || !promotedSrp) return;

        ppgs.forEach((p: ProductPPG) => {
            const edlpMetric = p.metrics.find((m) => m.id === edlpSrpTrue.id);
            const promotedMetric = p.metrics.find((m) => m.id === promotedSrp.id);
            const couponOverlayMetric = p.metrics.find((m) => m.id === couponOverlay?.id);
            const percentOfConsumptionMetric = p.metrics.find((m) => m.id === percentOfConsumption?.id);
            const promotedAurMetric = p.metrics.find((m) => m.id === promotedAur?.id);

            if (!edlpMetric || !promotedMetric) return;

            const edlpValue = Number(edlpMetric.value) || 0;
            let promotedValue = Number(promotedMetric.value);
            let couponValue = 0; // Initialize coupon value
            let promotedAurValue = Number(promotedAurMetric?.value);

            if (edlpValue === 0) {
                promotedValue = 0;
                couponValue = 0;
            } else if (manualOffer === 'price_off') {
                if (discountUnit === '%') {
                    couponValue = ((discount || 0) / 100) * edlpValue;
                    promotedValue = edlpValue - couponValue;
                } else if (discountUnit === 'abs') {
                    couponValue = discount || 0;
                    promotedValue = edlpValue - couponValue;
                }
            } else if (manualOffer === 'combination_offer') {
                const x = Number(buyGet?.[0]) || 0;
                const y = Number(buyGet?.[1]) || 0;

                if (x === 0) {
                    promotedValue = edlpValue;
                    couponValue = 0;
                } else if (comboUnit === 'abs') {
                    // BxGy (abs)
                    couponValue = y / x;
                    promotedValue = edlpValue - couponValue;
                } else if (comboUnit === '%') {
                    // BxGy (%)
                    couponValue = (y / 100) * edlpValue;
                    promotedValue = edlpValue - couponValue;
                } else if (comboUnit === 'item') {
                    // BxGy (item)
                    couponValue = (y / (x + y)) * edlpValue;
                    promotedValue = edlpValue - couponValue;
                } else {
                    couponValue = 0;
                    // promotedValue = edlpValue; // fallback for other units
                }
            } else if (manualOffer === 'buy_for' && buyFor) {
                const singleItemPrice = buyFor[1] / buyFor[0];
                promotedValue = singleItemPrice;
                couponValue = edlpValue - singleItemPrice;
            } else if (manualOffer === 'buy_save' && buySave) {
                const buyValue = Number(buySave?.[0]) || 0;
                const saveValue = Number(buySave?.[1]) || 0;

                if (buyValue === 0) {
                    promotedValue = edlpValue;
                    couponValue = 0;
                } else if (buySaveUnit === 'abs') {
                    const discountPercentage = (saveValue / buyValue) * 100;
                    couponValue = (edlpValue * discountPercentage) / 100;
                    promotedValue = edlpValue - couponValue;
                } else if (buySaveUnit === '%') {
                    const discountPercentage = saveValue;
                    couponValue = (edlpValue * discountPercentage) / 100;
                    promotedValue = edlpValue - couponValue;
                } else {
                    promotedValue = edlpValue;
                    couponValue = 0;
                }
            } else if (manualOffer === 'single') {
                promotedValue = edlpValue;
                couponValue = 0;
            }

            // Round and update the promoted SRP
            promotedMetric.value = promotedValue > 0 ? promotedValue.toFixed(2) : '0';
            // edlp_srp - ((edlpValue - promotedValue)*percent_of_consumption/100)
            promotedAurValue =
                edlpValue - ((edlpValue - promotedValue) * Number(percentOfConsumptionMetric?.value)) / 100;
            if (promotedAurMetric) {
                promotedAurMetric.value = promotedAurValue > 0 ? promotedAurValue.toFixed(2) : '0';
            }

            // Update the coupon overlay value if the metric exists
            if (couponOverlayMetric) {
                couponOverlayMetric.value = couponValue > 0 ? couponValue.toFixed(2) : '0';
            }
        });
    };

    const onGetMetricRecomValuesSuccess = (ppgs: ProductPPG[], triggeredFrom: MetricsApiTriggerPoints) => {
        // set Promoted srp if exists
        setPromotedSRPValue(ppgs);
        // reset editable spends for new promotions
        if (promoActiveTab === NEW_EVENT_PROMO_ID) {
            resetEditableSpendsToZero(ppgs, promoConfigDictionary);
        }

        let updatedPPgs: ProductPPG[] = [];
        switch (triggeredFrom) {
            case MetricsApiTriggerPoints.PPG: {
                const selectedMetrics = getMetricsObject(
                    selectedEventInfo?.currentPromoTactic?.ppgs?.[0]?.metrics,
                    true,
                );
                const selectedSpendMetrics = getMetricsObject(
                    selectedEventInfo?.currentPromoTactic?.ppgs?.[0]?.spendDetails,
                    false,
                );
                updatedPPgs = getUpdatedPPgsFromFormChange(
                    ppgs,
                    selectedEventInfo?.currentPromoTactic?.ppgs || [],
                    selectedEventInfo?.currentPromoTactic?.deletedPPgs || [],
                    selectedMetrics,
                    selectedSpendMetrics,
                );
                break;
            }
            case MetricsApiTriggerPoints.Date: {
                updatedPPgs = getUpdatedPPGsFromFormDateChange(ppgs);
                break;
            }
            case MetricsApiTriggerPoints.Tactic: {
                updatedPPgs = getUpdatedPPGsFromFormUpdateChange(ppgs);
                break;
            }
        }

        // sort the ppgs to existing order
        updatedPPgs = sortPPGsToExitingOrder(selectedEventInfo.currentPromoTactic.ppgs, updatedPPgs);

        // update default values for few metrics
        updatedPPgs = handleRecommendSuccessDefaultValue(updatedPPgs, promoConfigDictionary);

        const newTactic = { ...selectedEventInfo.currentPromoTactic, ppgs: _.cloneDeep(updatedPPgs) };

        setSelectedEventInfo({
            ...selectedEventInfo,
            currentPromoTactic: newTactic,
        });
        return _.cloneDeep(updatedPPgs);
    };

    const getPPGName = (ppgId: Number) =>
        tempEventProperties?.selectedDim1NestedAttributes?.find((item: any) => item.id === ppgId)?.value || '';
    const isTpmTab = Boolean(tpmTpoActiveTab?.key === 'tpm');

    //if rbac error, then fail closed and disable gated actions
    useEffect(() => {
        if (rbacError) {
            setRbacConditions({
                showCreatePlanButton: false,
                showApproveAndPublish: false,
                showApprovalPublishControl: false,
            });
        }
    }, [rbacError]);
    const resetCurrentState = () => {
        setSelectedEventInfo({ ...selectedEventInfo, currentPromoTactic: undefined as any });
    };

    useEffect(() => {
        if (selectedEventInfo?.promoTacticsIdNamePair?.length) {
            updateSelectedEventProperty('firstPromoTacticBackup', {
                ...selectedEventInfo.firstPromoTacticBackup,
                promoId: selectedEventInfo.promoTacticsIdNamePair[0]?.id,
            });
        }
    }, [selectedEventInfo?.promoTacticsIdNamePair]);

    const resetFilterObject = () => {
        const allSelectedObj: any = {};
        const selectAllObj: any = {};
        const applyFilterObj: any = {};
        uiDimensionConfigData?.filterKeys?.forEach((filter: any) => {
            allSelectedObj[filter.value] = new Set();
            applyFilterObj[filter.value] = {};
            selectAllObj[filter.value] = false;
        });
        setAvailableFilters(uiDimensionConfigData?.filterKeys ?? []);
        setAllCheckedAttribute(allSelectedObj);
        setApplyFilterObject(_.cloneDeep(applyFilterObj));
        setAllSelectedValueForAllAttribute(selectAllObj);
    };

    // todo : remove once testing is completed
    // //if attributes are same, then use that attribute else use default value
    // const updateFilterObject = (filterKeys: any[]) => {
    //     const allSelectedObj: any = {};
    //     const selectAllObj: any = {};
    //     const applyFilterObj: any = {};
    //     filterKeys?.forEach((filter: any) => {
    //         allSelectedObj[filter.value] =
    //             filter.value in allCheckedAttribute ? allCheckedAttribute[filter.value] : new Set();
    //         applyFilterObj[filter.value] = filter.value in applyFilterObject ? applyFilterObject[filter.value] : {};
    //         selectAllObj[filter.value] =
    //             filter.value in allSelectedValueForAllAttribute ? allSelectedValueForAllAttribute[filter.value] : false;
    //     });
    //     setAvailableFilters(filterKeys ?? []);
    //     setAllCheckedAttribute(allSelectedObj);
    //     setApplyFilterObject(_.cloneDeep(applyFilterObj));
    //     setAllSelectedValueForAllAttribute(selectAllObj);
    // };

    /**
     * Updates filter objects to support cross-tab filter persistence.
     *
     * This function implements a three-tier filter persistence strategy:
     * 1. Current tab filters (immediate state)
     * 2. Cross-tab persistent storage (final state objects)
     * 3. Default empty values (fallback)
     *
     * When switching between tabs (Mechanic Constraint, Product Constraint, Promo Library),
     * this ensures filters are preserved and restored appropriately.
     */
    // Plans-specific filter management function
    const updatePlansFilterObject = (filterKeys: any[]) => {
        // Initialize temporary objects to build the new filter state for Plans
        const tempAllCheckedAttribute: any = {};
        const tempSelectAllObj: any = {};
        const tempApplyFilterObj: any = {};

        // STEP 1: Save current Plans state to persistent storage
        Object.keys(plansIndexAllCheckedAttribute)?.forEach((key: string) => {
            backupPlansAllCheckedAttribute[key] = _.cloneDeep(plansIndexAllCheckedAttribute[key]);
            backupPlansAllSelectedValueForAllAttribute[key] = _.cloneDeep(
                plansIndexAllSelectedValueForAllAttribute[key],
            );
            backupPlansApplyFilterObject[key] = _.cloneDeep(plansIndexApplyFilterObject[key]);
        });

        // STEP 2: Build new filter state with persistence priority
        filterKeys?.forEach((filter: any) => {
            // Handle allCheckedAttribute (selected filter values)
            if (filter.value in plansIndexAllCheckedAttribute) {
                // Priority 1: Use current Plans selections
                tempAllCheckedAttribute[filter.value] = plansIndexAllCheckedAttribute[filter.value];
            } else if (
                filter.value in backupPlansAllCheckedAttribute &&
                backupPlansAllCheckedAttribute[filter.value]?.size
            ) {
                // Priority 2: Restore from Plans persistent storage
                tempAllCheckedAttribute[filter.value] = backupPlansAllCheckedAttribute[filter.value];
            } else {
                // Priority 3: Initialize with empty Set
                tempAllCheckedAttribute[filter.value] = new Set();
                backupPlansAllCheckedAttribute[filter.value] = new Set();
            }

            // Handle applyFilterObject (applied filter configurations)
            if (filter.value in plansIndexApplyFilterObject) {
                // Priority 1: Use current Plans applied filters
                tempApplyFilterObj[filter.value] = plansIndexApplyFilterObject[filter.value];
            } else if (
                filter.value in backupPlansApplyFilterObject &&
                Object.keys(backupPlansApplyFilterObject[filter.value] ?? {}).length > 0
            ) {
                // Priority 2: Restore from Plans persistent storage
                tempApplyFilterObj[filter.value] = backupPlansApplyFilterObject[filter.value];
            } else {
                // Priority 3: Initialize with empty object
                tempApplyFilterObj[filter.value] = {};
                backupPlansApplyFilterObject[filter.value] = {};
            }

            // Handle allSelectedValueForAllAttribute ("Select All" states)
            if (filter.value in plansIndexAllSelectedValueForAllAttribute) {
                // Priority 1: Use current Plans "Select All" state
                tempSelectAllObj[filter.value] = plansIndexAllSelectedValueForAllAttribute[filter.value];
            } else if (
                filter.value in backupPlansAllSelectedValueForAllAttribute &&
                backupPlansAllSelectedValueForAllAttribute[filter.value]
            ) {
                // Priority 2: Restore from Plans persistent storage
                tempSelectAllObj[filter.value] = backupPlansAllSelectedValueForAllAttribute[filter.value];
            } else {
                // Priority 3: Initialize with false
                tempSelectAllObj[filter.value] = false;
                backupPlansAllSelectedValueForAllAttribute[filter.value] = false;
            }
        });

        // STEP 3: Update Plans-specific states
        setPlansIndexAllCheckedAttribute(_.cloneDeep(tempAllCheckedAttribute));
        setPlansIndexApplyFilterObject(_.cloneDeep(tempApplyFilterObj));
        setPlansIndexAllSelectedValueForAllAttribute(_.cloneDeep(tempSelectAllObj));

        // STEP 4: Update Plans-specific persistent storage
        setBackupPlansAllCheckedAttribute(_.cloneDeep(backupPlansAllCheckedAttribute));
        setBackupPlansAllSelectedValueForAllAttribute(_.cloneDeep(backupPlansAllSelectedValueForAllAttribute));
        setBackupPlansApplyFilterObject(_.cloneDeep(backupPlansApplyFilterObject));
    };

    // Helper function to build filter state for a single filter
    const buildFilterStateForFilter = (
        filter: any,
        tempAllCheckedAttribute: any,
        tempSelectAllObj: any,
        tempApplyFilterObj: any,
        usePlansData: boolean = false,
    ) => {
        if (usePlansData) {
            // Use Plans data for this filter
            tempAllCheckedAttribute[filter.value] = plansIndexAllCheckedAttribute[filter.value] || new Set();
            tempApplyFilterObj[filter.value] = plansIndexApplyFilterObject[filter.value] || {};
            tempSelectAllObj[filter.value] = plansIndexAllSelectedValueForAllAttribute[filter.value] || false;
            return;
        }

        // Use existing logic for non-Plans filters
        // Handle allCheckedAttribute (selected filter values)
        if (filter.value in allCheckedAttribute) {
            tempAllCheckedAttribute[filter.value] = allCheckedAttribute[filter.value];
        } else if (filter.value in finalAllCheckedAttribute && finalAllCheckedAttribute[filter.value]?.size) {
            tempAllCheckedAttribute[filter.value] = finalAllCheckedAttribute[filter.value];
        } else {
            tempAllCheckedAttribute[filter.value] = new Set();
            finalAllCheckedAttribute[filter.value] = new Set();
        }

        // Handle applyFilterObject (applied filter configurations)
        if (filter.value in applyFilterObject) {
            tempApplyFilterObj[filter.value] = applyFilterObject[filter.value];
        } else if (
            filter.value in finalApplyFilterObject &&
            Object.keys(finalApplyFilterObject[filter.value] ?? {}).length > 0
        ) {
            tempApplyFilterObj[filter.value] = finalApplyFilterObject[filter.value];
        } else {
            tempApplyFilterObj[filter.value] = {};
            finalApplyFilterObject[filter.value] = {};
        }

        // Handle allSelectedValueForAllAttribute ("Select All" states)
        if (filter.value in allSelectedValueForAllAttribute) {
            tempSelectAllObj[filter.value] = allSelectedValueForAllAttribute[filter.value];
        } else if (
            filter.value in finalAllSelectedValueForAllAttribute &&
            finalAllSelectedValueForAllAttribute[filter.value]
        ) {
            tempSelectAllObj[filter.value] = finalAllSelectedValueForAllAttribute[filter.value];
        } else {
            tempSelectAllObj[filter.value] = false;
            finalAllSelectedValueForAllAttribute[filter.value] = false;
        }
    };

    // Smart filter sharing function: Share Plans filters to Tools when compatible
    const updateFilterObjectWithPlansSharing = (filterKeys: any[]) => {
        // Check if Plans has any applied filters
        const hasPlansFilters =
            Object.keys(plansIndexAllCheckedAttribute).some((key) => plansIndexAllCheckedAttribute[key]?.size > 0) ||
            Object.keys(plansIndexApplyFilterObject).some(
                (key) => Object.keys(plansIndexApplyFilterObject[key] ?? {}).length > 0,
            );

        if (!hasPlansFilters) {
            // No Plans filters to share, use normal updateFilterObject
            updateFilterObject(filterKeys);
            return;
        }

        // Find common filters between Plans and Tools
        const commonFilters = filterKeys.filter(
            (toolFilter) =>
                plansIndexAllCheckedAttribute[toolFilter.value] || plansIndexApplyFilterObject[toolFilter.value],
        );

        if (commonFilters.length === 0) {
            // No common filters, use normal updateFilterObject
            updateFilterObject(filterKeys);
            return;
        }

        // Initialize temporary objects to build the new filter state
        const tempAllCheckedAttribute: any = {};
        const tempSelectAllObj: any = {};
        const tempApplyFilterObj: any = {};

        // Build new filter state with Plans sharing for common filters
        filterKeys.forEach((filter: any) => {
            const isCommonFilter = commonFilters.some((cf) => cf.value === filter.value);

            // Use helper function to build filter state
            buildFilterStateForFilter(
                filter,
                tempAllCheckedAttribute,
                tempSelectAllObj,
                tempApplyFilterObj,
                isCommonFilter, // Use Plans data if it's a common filter
            );
        });

        // Update all state variables with the new computed values
        setAvailableFilters(filterKeys ?? []);

        // Update current tab filter states
        setAllCheckedAttribute(_.cloneDeep(tempAllCheckedAttribute));
        setApplyFilterObject(_.cloneDeep(tempApplyFilterObj));
        setAllSelectedValueForAllAttribute(_.cloneDeep(tempSelectAllObj));

        // Update persistent storage
        setFinalAllCheckedAttribute(_.cloneDeep(finalAllCheckedAttribute));
        setFinalApplyFilterObject(_.cloneDeep(finalApplyFilterObject));
        setFinalAllSelectedValueForAllAttribute(_.cloneDeep(finalAllSelectedValueForAllAttribute));
    };

    const updateFilterObject = (filterKeys: any[]) => {
        // Initialize temporary objects to build the new filter state
        const tempAllCheckedAttribute: any = {}; // Selected filter values for current tab
        const tempSelectAllObj: any = {}; // "Select All" checkbox states for current tab
        const tempApplyFilterObj: any = {}; // Applied filter configurations for current tab

        // STEP 1: Preserve current filter state in persistent storage
        // Before switching tabs, save the current filter selections to final objects
        // This acts as an in-memory cross-tab storage mechanism
        Object.keys(allCheckedAttribute)?.forEach((key: string) => {
            finalAllCheckedAttribute[key] = _.cloneDeep(allCheckedAttribute[key]);
            finalAllSelectedValueForAllAttribute[key] = _.cloneDeep(allSelectedValueForAllAttribute[key]);
            finalApplyFilterObject[key] = _.cloneDeep(applyFilterObject[key]);
        });

        // STEP 2: Build new filter state with persistence priority
        // For each filter key available in the new tab, apply this priority:
        // 1st: Use current tab state (if available)
        // 2nd: Use persistent cross-tab state (if previously saved)
        // 3rd: Create default empty state
        filterKeys?.forEach((filter: any) => {
            // Use helper function to build filter state (always use existing logic, not Plans data)
            buildFilterStateForFilter(
                filter,
                tempAllCheckedAttribute,
                tempSelectAllObj,
                tempApplyFilterObj,
                false, // Don't use Plans data for regular updateFilterObject
            );
        });

        // STEP 3: Update all state variables with the new computed values
        setAvailableFilters(filterKeys ?? []); // Set current tab's available filters

        // Update current tab filter states
        setAllCheckedAttribute(_.cloneDeep(tempAllCheckedAttribute));
        setApplyFilterObject(_.cloneDeep(tempApplyFilterObj));
        setAllSelectedValueForAllAttribute(_.cloneDeep(tempSelectAllObj));

        // Update persistent cross-tab storage
        setFinalAllCheckedAttribute(_.cloneDeep(finalAllCheckedAttribute));
        setFinalAllSelectedValueForAllAttribute(_.cloneDeep(finalAllSelectedValueForAllAttribute));
        setFinalApplyFilterObject(_.cloneDeep(finalApplyFilterObject));
    };

    // Smart filter sharing function: Share Tools filters to Plans when compatible
    const updatePlansFilterObjectWithToolsSharing = (filterKeys: any[]) => {
        // Check if Tools has any applied filters
        const hasToolsFilters =
            Object.keys(allCheckedAttribute).some((key) => allCheckedAttribute[key]?.size > 0) ||
            Object.keys(applyFilterObject).some((key) => Object.keys(applyFilterObject[key] ?? {}).length > 0);

        if (!hasToolsFilters) {
            // No Tools filters to share, use normal updatePlansFilterObject
            updatePlansFilterObject(filterKeys);
            return;
        }

        // Find common filters between Tools and Plans
        const commonFilters = filterKeys.filter(
            (planFilter) => allCheckedAttribute[planFilter.value] || applyFilterObject[planFilter.value],
        );

        if (commonFilters.length === 0) {
            // No common filters, use normal updatePlansFilterObject
            updatePlansFilterObject(filterKeys);
            return;
        }

        // Initialize temporary objects to build the new filter state
        const tempAllCheckedAttribute: any = {};
        const tempSelectAllObj: any = {};
        const tempApplyFilterObj: any = {};

        // STEP 1: Save current Plans state to persistent storage
        Object.keys(plansIndexAllCheckedAttribute)?.forEach((key: string) => {
            backupPlansAllCheckedAttribute[key] = _.cloneDeep(plansIndexAllCheckedAttribute[key]);
            backupPlansAllSelectedValueForAllAttribute[key] = _.cloneDeep(
                plansIndexAllSelectedValueForAllAttribute[key],
            );
            backupPlansApplyFilterObject[key] = _.cloneDeep(plansIndexApplyFilterObject[key]);
        });

        // Build new filter state with Tools sharing for common filters
        filterKeys.forEach((filter: any) => {
            const isCommonFilter = commonFilters.some((cf) => cf.value === filter.value);

            if (isCommonFilter) {
                // Use Tools data for common filters
                tempAllCheckedAttribute[filter.value] = allCheckedAttribute[filter.value] || new Set();
                tempApplyFilterObj[filter.value] = applyFilterObject[filter.value] || {};
                tempSelectAllObj[filter.value] = allSelectedValueForAllAttribute[filter.value] || false;
            } else {
                // Use Plans persistence logic for non-common filters
                if (filter.value in plansIndexAllCheckedAttribute) {
                    tempAllCheckedAttribute[filter.value] = plansIndexAllCheckedAttribute[filter.value];
                } else if (
                    filter.value in backupPlansAllCheckedAttribute &&
                    backupPlansAllCheckedAttribute[filter.value]?.size
                ) {
                    tempAllCheckedAttribute[filter.value] = backupPlansAllCheckedAttribute[filter.value];
                } else {
                    tempAllCheckedAttribute[filter.value] = new Set();
                    backupPlansAllCheckedAttribute[filter.value] = new Set();
                }

                if (filter.value in plansIndexApplyFilterObject) {
                    tempApplyFilterObj[filter.value] = plansIndexApplyFilterObject[filter.value];
                } else if (
                    filter.value in backupPlansApplyFilterObject &&
                    Object.keys(backupPlansApplyFilterObject[filter.value] ?? {}).length > 0
                ) {
                    tempApplyFilterObj[filter.value] = backupPlansApplyFilterObject[filter.value];
                } else {
                    tempApplyFilterObj[filter.value] = {};
                    backupPlansApplyFilterObject[filter.value] = {};
                }

                if (filter.value in plansIndexAllSelectedValueForAllAttribute) {
                    tempSelectAllObj[filter.value] = plansIndexAllSelectedValueForAllAttribute[filter.value];
                } else if (
                    filter.value in backupPlansAllSelectedValueForAllAttribute &&
                    backupPlansAllSelectedValueForAllAttribute[filter.value]
                ) {
                    tempSelectAllObj[filter.value] = backupPlansAllSelectedValueForAllAttribute[filter.value];
                } else {
                    tempSelectAllObj[filter.value] = false;
                    backupPlansAllSelectedValueForAllAttribute[filter.value] = false;
                }
            }
        });

        // STEP 2: Update Plans-specific states
        setPlansIndexAllCheckedAttribute(_.cloneDeep(tempAllCheckedAttribute));
        setPlansIndexApplyFilterObject(_.cloneDeep(tempApplyFilterObj));
        setPlansIndexAllSelectedValueForAllAttribute(_.cloneDeep(tempSelectAllObj));

        // STEP 3: Update Plans-specific persistent storage
        setBackupPlansAllCheckedAttribute(_.cloneDeep(backupPlansAllCheckedAttribute));
        setBackupPlansAllSelectedValueForAllAttribute(_.cloneDeep(backupPlansAllSelectedValueForAllAttribute));
        setBackupPlansApplyFilterObject(_.cloneDeep(backupPlansApplyFilterObject));
    };

    useEffect(() => {
        if (!Object.keys(applyFilterObject).length && uiDimensionConfigData?.filterKeys?.length) resetFilterObject();
    }, [uiDimensionConfigData?.filterKeys]);

    const isOptimizationInProgress = pollingStatus === OptimizationPollingStatus.IN_PROGRESS;

    const getUpliftValRecomm = async (payload: {
        mechanicType: string;
        ppgs: ProductPPG[] | undefined;
        onUpliftComplete?: (tactic: PromoTactic) => void;
    }): Promise<void> => {
        const currentPromoTactic = selectedEventInfo?.currentPromoTactic;
        const promotion_mechanic = payload.mechanicType;
        const tactic_type = promoConfigDictionary.tactics[Number(currentPromoTactic?.tacticType)];
        const promoType = Number(currentPromoTactic?.promoType);
        const payloadPPgs = payload.ppgs
            ?.filter((item) => item.isSelected)
            .map((ppg) => {
                return {
                    ppg_id: ppg.id,
                    promo_metrics: ppg.metrics.reduce((acc, metric) => {
                        if (
                            [
                                'edlp_srp',
                                'feat_acv_percent',
                                'disp_acv_percent',
                                'percent_of_consumption',
                                'promoted_srp',
                                'edlp_srp_true',
                            ].includes(
                                promoConfigDictionary.promoMetricMapping?.[promoType].promoMetrics[metric.id].name_key,
                            )
                        ) {
                            acc = {
                                ...acc,
                                [promoConfigDictionary.promoMetricMapping?.[promoType].promoMetrics[metric.id]
                                    .name_key]: Number(metric.value),
                            };
                        }
                        return acc;
                    }, {}),
                };
            });
        if (
            (currentPromoTactic.manualOffer === 'price_off' && currentPromoTactic.discount != null) ||
            (currentPromoTactic.manualOffer === 'combination_offer' &&
                currentPromoTactic.buyGet?.[0] &&
                currentPromoTactic.buyGet?.[1]) ||
            (currentPromoTactic.manualOffer === PromoInfoFormFieldNames.BuyFor &&
                currentPromoTactic.buyFor?.[0] &&
                currentPromoTactic.buyFor?.[1]) ||
            (currentPromoTactic.manualOffer === 'buy_save' &&
                currentPromoTactic.buySave?.[0] &&
                currentPromoTactic.buySave?.[1]) ||
            currentPromoTactic.manualOffer === 'single'
        ) {
            const response = await axios.post(tradePromoOptimizationUrls.getUpliftRecommendation, {
                promotion_mechanic,
                tactic_type,
                dim2_attribute_name: selectedPlanInfo?.dim2Filter.value[0],
                ppgs: payloadPPgs,
                promotion_type: promoConfigDictionary.promotions[promoType],
                offer_details: {
                    offer_mechanic: currentPromoTactic?.offer,
                    manual_details: {
                        type: currentPromoTactic?.manualOffer,
                        value: currentPromoTactic?.discount,
                        buy: currentPromoTactic?.buyGet?.[0] || null,
                        get: currentPromoTactic?.buyGet?.[1] || null,
                        is_value_percentage: currentPromoTactic?.discountUnit === '%',
                        combo_unit: currentPromoTactic?.buyGet?.length ? currentPromoTactic?.comboUnit : null,
                        bf_buy: currentPromoTactic?.buyFor?.[0] || null,
                        bf_for: currentPromoTactic?.buyFor?.[1] || null,
                        bs_buy: currentPromoTactic?.buySave?.[0] || null,
                        bs_save: currentPromoTactic?.buySave?.[1] || null,
                        bs_combo_unit: currentPromoTactic?.buySave?.length ? currentPromoTactic?.buySaveUnit : null,
                    },
                },
            });
            return response.data;
        }
        throw new Error('Uplift recommendation request skipped: incomplete offer details');
    };

    const upliftModelMutation = useMutation({
        mutationFn: getUpliftValRecomm,
        onSuccess: (data: any, variables: any) => {
            if (selectedEventInfo?.currentPromoTactic) {
                const promoType = Number(selectedEventInfo?.currentPromoTactic.promoType);
                const tpoAdjustedBaseUnitsKey = getPromoMetricByNameKeyName(
                    promoConfigDictionary,
                    tpoPromoMetricKeys.TPO_ADJUSTED_BASE_UNITS,
                );
                const tpoIncrementalUnits = getPromoMetricByNameKeyName(
                    promoConfigDictionary,
                    tpoPromoMetricKeys.TPO_INCREMENTAL_UNITS,
                );
                const updatedPPgs = selectedEventInfo.currentPromoTactic.ppgs.map((ppg) => {
                    const newUpliftVal = data.data.find((item: any) => item.ppg_id === ppg.id)?.tpo_uplift_percent;

                    if (newUpliftVal !== undefined) {
                        const updatedMetrics = ppg.metrics.map((metric) => {
                            const metricKey =
                                promoConfigDictionary.promoMetricMapping?.[promoType]?.promoMetrics?.[metric.id]
                                    ?.name_key;

                            // Update uplift percent
                            if (metricKey === tpoPromoMetricKeys.TPO_Uplift_Percent) {
                                return {
                                    ...metric,
                                    value: newUpliftVal,
                                };
                            }

                            // Compute and update incremental units
                            if (metricKey === tpoPromoMetricKeys.TPO_INCREMENTAL_UNITS) {
                                const tpoAdjustedBaseUnits = Number(
                                    ppg.metrics.find((m) => m.id === tpoAdjustedBaseUnitsKey.id)?.value ?? 0,
                                );

                                const newIncrementalUnits = Math.floor((newUpliftVal * tpoAdjustedBaseUnits) / 100);

                                return {
                                    ...metric,
                                    value: newIncrementalUnits,
                                };
                            }

                            return metric;
                        });

                        return { ...ppg, metrics: updatedMetrics };
                    }

                    return ppg;
                });

                const newSelectedEventInfo = _.cloneDeep(selectedEventInfo);

                // Start with the updatedPPgs
                newSelectedEventInfo.currentPromoTactic.ppgs = _.cloneDeep(updatedPPgs);

                const cascadedUpdatedPPGs = newSelectedEventInfo.currentPromoTactic.ppgs.map((ppg) => {
                    const tpoAdjustedBaseUnits = Number(
                        ppg.metrics.find((m) => m.id === tpoAdjustedBaseUnitsKey.id)?.value ?? 0,
                    );

                    const newUpliftVal = data.data.find((item: any) => item.ppg_id === ppg.id)?.tpo_uplift_percent;

                    if (newUpliftVal === undefined || isNaN(tpoAdjustedBaseUnits)) {
                        return ppg; // Skip update for invalid data
                    }

                    const newIncrementalUnits = Math.floor((newUpliftVal * tpoAdjustedBaseUnits) / 100);

                    // Returns full list of PPGs with updated metric for this one PPG
                    const updatedPPGList = handleMetricInputChange(
                        promoConfigDictionary,
                        newSelectedEventInfo,
                        tpoIncrementalUnits.id,
                        ppg.name,
                        newIncrementalUnits?.toString(),
                        true, // its metric trigger
                    );

                    // Safe access with fallback
                    return Array.isArray(updatedPPGList) ? updatedPPGList.find((p) => p.name === ppg.name) ?? ppg : ppg;
                });

                newSelectedEventInfo.currentPromoTactic.ppgs = cascadedUpdatedPPGs;

                const updatedTactic = {
                    ...selectedEventInfo.currentPromoTactic,
                    ppgs: newSelectedEventInfo.currentPromoTactic.ppgs,
                };

                updateSelectedEventProperty('currentPromoTactic', updatedTactic);
                variables?.onUpliftComplete?.(updatedTactic);
            }
        },
        onError: (err: any) => {
            console.error(err, 'Uplift Api Failed');
        },
    });

    return (
        <TPODataContext.Provider
            value={{
                isKpiAbsView,
                setIsKpiAbsView,
                viewMode: isViewMode(),
                isCurrentPromoFormDisabled: isCurrentPromoDisabled(),
                tabs: tpoTabs,
                activeTab,
                setActiveTab,
                breadcrumbItems,
                dimensionConfigData,
                setDimensionConfigData,
                kpiConfigData,
                kpiRawData,
                playbookKpiConfigData,
                configDataExists,
                selectedPlanInfo,
                setSelectedPlanInfo,
                setEventViewType,
                eventViewType,
                planSummarySubTab,
                setPlanSummarySubTab,
                enableBumpReport,
                setEnableBumpReport,
                selectedEventInfo,
                setSelectedEventInfo,
                updateSelectedEventProperty,
                updatePlanProperty,
                kpiConfigLoading,
                setShowCalenderChanges,
                showCalenderChanges,
                showChanges,
                setShowChanges,
                isToolsViewAbsolute,
                setIsToolsViewAbsolute,
                promoActiveTab,
                setPromoActiveTab,
                promoConfig,
                onGetMetricRecomValuesSuccess,
                isValid,
                setIsValid,
                planMetaDataLoading,
                planIdFromUrl: planId ?? null,
                promoLabelMapping,
                tempEventProperties,
                setTempEventProperties,
                selectedPromoPPGIds,
                setSelectedPromoPPGIds,
                promoConfigDictionary,
                errorMessage,
                setErrorMessage,
                tpmTpoActiveTab,
                setTpmTpoActiveTab,
                isTpmTab,
                rbacConditions,
                rbacLoading,
                approvalPublishEnabled,
                approvalPublishToggleLoading,
                resetCurrentState,
                selectedEventLibraryEventInfo,
                setSelectedEventLibraryEventInfo,
                isCurrentViewEventLibrary,
                setShowSyncedPlans,
                showSyncedPlans,
                optimizedPlanId,
                setOptimizedPlanId,
                setShowOptimizedPlanCalender,
                showOptimizedPlanCalender,
                promoConfigLoading,
                promoPopupErrors,
                setPromoPopupErrors,
                clearPromoPopupErrors,
                promoPopupWarnings,
                setPromoPopupWarnings,
                clearPromoPopupWarnings,
                resetFilterObject,
                updateFilterObject,
                updatePlansFilterObject,
                updateFilterObjectWithPlansSharing,
                updatePlansFilterObjectWithToolsSharing,
                availableFilters,
                allCheckedAttribute,
                setAllCheckedAttribute,
                applyFilterObject,
                setApplyFilterObject,
                allSelectedValueForAllAttribute,
                setAllSelectedValueForAllAttribute,
                uiDimensionConfigData,
                setUiDimensionConfigData,
                // Plans-specific filter states
                plansIndexAllCheckedAttribute,
                setPlansIndexAllCheckedAttribute,
                plansIndexAllSelectedValueForAllAttribute,
                setPlansIndexAllSelectedValueForAllAttribute,
                plansIndexApplyFilterObject,
                setPlansIndexApplyFilterObject,
                backupPlansAllCheckedAttribute,
                setBackupPlansAllCheckedAttribute,
                backupPlansAllSelectedValueForAllAttribute,
                setBackupPlansAllSelectedValueForAllAttribute,
                backupPlansApplyFilterObject,
                setBackupPlansApplyFilterObject,
                planEditMetaData,
                setPlanEditMetaData,
                markTpoPlanContentMutated,
                pollingStatus,
                setPollingStatus,
                isOptimizationInProgress,
                upliftModelMutation,
                optimizationError,
                setOptimizationError,
                planRefreshLoading,
                setPlanRefreshLoading,
                infeasibilityPollingStatus,
                setInfeasibilityPollingStatus,
                infeasibilityData,
                setInfeasibilityData,
                filterUIDimensionsLoading,
                setFilterUIDimensionsLoading,
                divisionMetricMapping,
                hasCurrentPromoTacticInitialized,
                setHasCurrentPromoTacticInitialized,
                skipSRPUpdateRef,
                finalCalenderStatues,
                setFinalCalenderStatues,
                merchMDFPromoCount,
                setMerchMDFPromoCount,
                validatePlanId,
                setValidatePlanId,
                validatePlanPollingStatus,
                setValidatePlanPollingStatus,
                refetchTpmStatus,
            }}
        >
            <TpmRefreshStatusPoller onStatusChange={handleTpmStatusChange} />
            {tpmRefreshRunning ? (
                <div className="bg-white flex items-center justify-center rounded-lg min-h-[calc(100vh-120px)]">
                    <p className="m-0 text-lg font-semibold text-greyFont">
                        {tpmRefreshMessage || 'TPM refresh in progress...'}
                    </p>
                </div>
            ) : (
                children
            )}
        </TPODataContext.Provider>
    );
};

export { TPOContext, TPODataContext };
